import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, ChevronRight } from 'lucide-react';

const HUBSPOT_SCRIPT_SRC = 'https://js-na2.hsforms.net/forms/embed/244560927.js';

const volunteerCards = [
  {
    title: 'Volunteer at Our Home Library Center',
    body: 'Sort, curate, and pack books for children from birth through eighth grade. Opportunities are available for individuals, families, and groups.',
  },
  {
    title: 'Help at a Community Distribution',
    body: 'Welcome families and create a joyful experience as children choose and receive books of their own.',
  },
  {
    title: 'Bring Your Team',
    body: 'Corporate, school, civic, and community groups can volunteer together for a hands-on experience with visible impact.',
  },
  {
    title: 'Share Your Skills',
    body: 'Contribute your expertise in education, technology, logistics, communications, fundraising, events, or another area.',
  },
];

export default function Volunteer() {
  useEffect(() => {
    // Guard against double-injection (e.g. React StrictMode double-invoking
    // effects in development, or the user navigating away and back).
    let script = document.querySelector(`script[src="${HUBSPOT_SCRIPT_SRC}"]`);

    if (!script) {
      script = document.createElement('script');
      script.src = HUBSPOT_SCRIPT_SRC;
      script.defer = true;
      document.body.appendChild(script);
    }

    // Deliberately no cleanup here: removing this third-party embed script
    // does not unload its JS or reset its global state. If the effect
    // re-runs on remount and re-appends the script, it re-executes into an
    // already-initialized environment and the form can silently fail to
    // render. Leaving one cached script resident is the safer trade.
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#2c5570] to-[#14919f] text-white pt-[140px] pb-[80px] bg-cover bg-top" style={{ backgroundImage: `url('/bg-contact-hero.jpg')` }}>
        <div className="absolute inset-0 bg-gradient-to-r from-[#2c5570]/85 to-[#14919f]/85"></div>
        <div className="relative mx-auto max-w-[1240px] px-12 text-center">
          <span className="thlp-pill thlp-pill--gold inline-flex mb-6">Get Involved</span>
          <h1 className="text-[40px] sm:text-[56px] font-extrabold leading-tight mb-6" style={{ letterSpacing: '0' }}>
            Volunteer
          </h1>
          <p className="text-[19px] sm:text-[22px] text-white/90 leading-[1.6] max-w-[800px] mx-auto mb-16">
            We're building our volunteer program and will have opportunities for book sorting, packing, and community events.
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="bg-white py-[100px]">
        <div className="mx-auto max-w-[900px] px-8 text-center">
          <h2 className="text-[32px] sm:text-[40px] font-bold text-[#2c5570] mb-8 leading-tight">
            Help Put Books Into Children’s Hands and Homes
          </h2>
          <div className="space-y-6 text-left sm:text-center">
            <p className="text-lg text-[#4a4a4a] leading-[1.7]">
              Every home library begins with people who care.
            </p>
            <p className="text-lg text-[#4a4a4a] leading-[1.7]">
              Volunteers help receive and sort books, organize them by age and interest, pack home libraries with care, prepare materials for families, and support distributions through schools and trusted community partners.
            </p>
            <p className="text-lg text-[#4a4a4a] leading-[1.7]">
              Whether you volunteer on your own, with your family, coworkers, school, or community group, your time helps move books from our shelves into the hands and homes of children who will make them their own.
            </p>
          </div>
        </div>
      </section>

      {/* Volunteer Opportunity Cards */}
      <section className="bg-[#F6F7F9] py-[100px]">
        <div className="mx-auto max-w-[1240px] px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {volunteerCards.map((card) => (
              <div
                key={card.title}
                className="bg-white p-8 border border-[#e5e5e5] rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.06)] flex flex-col"
              >
                <h3 className="text-xl font-bold text-[#2c5570] mb-4">{card.title}</h3>
                <p className="text-base text-[#4a4a4a] leading-[1.7]">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Volunteer Form + Info Section */}
      <section className="bg-white py-[100px]">
        <div className="mx-auto max-w-[1240px] px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-16">
            {/* Left Column: Volunteer Form */}
            <div>
              <div className="bg-white p-12 border border-[#e5e5e5] rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.06)]">
                <div className="hs-form-frame" data-region="na2" data-form-id="d43fdfc8-c617-4a9d-8770-83a8be4970c3" data-portal-id="244560927"></div>
              </div>
            </div>

            {/* Right Column: Contact Info */}
            <div className="py-8">
              {/* Contact Information */}
              <div className="mb-12">
                <h3 className="text-[22px] font-bold text-[#2c5570] mb-6">Contact Information</h3>

                <div className="space-y-6">
                  <a href="mailto:info@thehomelibraryproject.org" className="flex items-center gap-4 group">
                    <div className="flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#14919f]" />
                    </div>
                    <div>
                      <div className="text-sm text-[#666] mb-1">Email</div>
                      <div className="text-base text-[#2c5570] group-hover:text-[#14919f] transition-colors">
                        info@thehomelibraryproject.org
                      </div>
                    </div>
                  </a>

                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#14919f]" />
                    </div>
                    <div>
                      <div className="text-sm text-[#666] mb-1">Location</div>
                      <div className="text-base text-[#666]">Delaware</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-[#e5e5e5] my-12"></div>

              {/* Quick Links Card */}
              <div className="bg-gradient-to-br from-[#f0f8fa] to-white p-10 rounded-xl shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
                <h3 className="text-xl font-bold text-[#2c5570] mb-6">Looking for something specific?</h3>

                <div className="space-y-4">
                  <a href="/about#mission" className="flex items-center gap-2 text-base text-[#14919f] hover:text-[#117a86] hover:pl-2 transition-all duration-200 group">
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    <span>Learn about our mission</span>
                  </a>
                  <a href="/#solution" className="flex items-center gap-2 text-base text-[#14919f] hover:text-[#117a86] hover:pl-2 transition-all duration-200 group">
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    <span>See how we work</span>
                  </a>
                  <Link to="/donate" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2 text-base text-[#14919f] hover:text-[#117a86] hover:pl-2 transition-all duration-200 group">
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    <span>Make a donation</span>
                  </Link>
                  <a href="/about#team" className="flex items-center gap-2 text-base text-[#14919f] hover:text-[#117a86] hover:pl-2 transition-all duration-200 group">
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    <span>Meet the founders</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Section */}
      <section className="relative bg-gradient-to-r from-[#2c5570] to-[#14919f] text-white py-[100px]">
        <div className="relative mx-auto max-w-[800px] px-8 text-center">
          <h2 className="text-[32px] sm:text-[40px] font-bold leading-tight mb-6">
            Be Part of What Delaware Is Building
          </h2>
          <p className="text-lg sm:text-xl text-white/90 leading-[1.7]">
            Whether you sort one box, pack one bag, or help at one distribution, you are helping build a future in which every Delaware child grows up with books of their own.
          </p>
        </div>
      </section>
    </div>
  );
}
